/**
 * App Version Filters Component
 *
 * Filter controls for app versions list with platform, active status, and mandatory filters.
 */

import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { Platform } from '@/types/app-version';

interface AppVersionFiltersProps {
  platform?: Platform;
  isActive?: boolean;
  isMandatory?: boolean;
  onFilterChange: (filters: {
    platform?: Platform;
    isActive?: boolean;
    isMandatory?: boolean;
  }) => void;
  onClearFilters: () => void;
}

/**
 * App Version Filters
 * Renders filter dropdowns for platform, active status, and mandatory status
 */
export function AppVersionFilters({
  platform,
  isActive,
  isMandatory,
  onFilterChange,
  onClearFilters,
}: AppVersionFiltersProps) {
  const hasActiveFilters =
    platform !== undefined || isActive !== undefined || isMandatory !== undefined;

  return (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
      {/* Platform Filter */}
      <div className="w-full sm:w-48">
        <Select
          value={platform || 'all'}
          onValueChange={value => {
            onFilterChange({
              platform: value === 'all' ? undefined : (value as Platform),
              isActive,
              isMandatory,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="All Platforms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            <SelectItem value="ios">iOS</SelectItem>
            <SelectItem value="android">Android</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active Status Filter */}
      <div className="w-full sm:w-48">
        <Select
          value={isActive === undefined ? 'all' : isActive ? 'active' : 'inactive'}
          onValueChange={value => {
            onFilterChange({
              platform,
              isActive: value === 'all' ? undefined : value === 'active',
              isMandatory,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mandatory Filter */}
      <div className="w-full sm:w-48">
        <Select
          value={isMandatory === undefined ? 'all' : isMandatory ? 'mandatory' : 'optional'}
          onValueChange={value => {
            onFilterChange({
              platform,
              isActive,
              isMandatory: value === 'all' ? undefined : value === 'mandatory',
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="mandatory">Mandatory</SelectItem>
            <SelectItem value="optional">Optional</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={onClearFilters} className="gap-2">
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
